import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import {
	clearStoredToken,
	decodeJwtPayload,
	getStoredToken,
	isAccessTokenAlive,
	onAuthLost,
	refreshAccessToken,
	setStoredToken,
} from "../../services/authToken";
import { resetGoWebSocket } from "../../services/socketv2";
import { disconnectAllSharedSockets } from "../../services/socketManager";
import toastError from "../../errors/toastError";
import { isMasterUser, MASTER_HOME } from "../../helpers/masterUser";
import { normalizeChatLayoutMode } from "../../helpers/chatLayoutMode";

function userFromAccessToken(token) {
	const payload = decodeJwtPayload(token);
	if (!payload?.id) return null;
	return {
		id: Number(payload.id),
		name: payload.username || "",
		profile: payload.profile || "",
		companyId: payload.companyId ?? undefined,
	};
}

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	useEffect(() => {
		return onAuthLost(() => {
			setIsAuth(false);
			setUser({});
		});
	}, []);

	useEffect(() => {
		(async () => {
			const token = getStoredToken();
			if (!token) {
				setLoading(false);
				return;
			}

			try {
				const data = await refreshAccessToken();
				setStoredToken(data.token);
				setUser(data.user);
				setIsAuth(true);
			} catch {
				// Refresh cookie pode falhar; não desloga se o access token ainda vale
				if (isAccessTokenAlive(token)) {
					const fallbackUser = userFromAccessToken(token);
					if (fallbackUser) {
						setUser(fallbackUser);
						setIsAuth(true);
					} else {
						clearStoredToken();
						setIsAuth(false);
					}
				} else {
					clearStoredToken();
					setIsAuth(false);
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		if (!user?.id) return undefined;

		const socket = openSocket({
			scope: "useauth",
			userId: user.id,
			component: "useAuth",
			companyId: user.companyId ?? "",
		});
		if (!socket) return undefined;

		socket.on("user", (data) => {
			if (data.action === "update" && data.user && data.user.id === user.id) {
				if (
					data.user.companyId == null ||
					data.user.companyId === user.companyId
				) {
					setUser(data.user);
				}
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user?.id, user?.companyId]);

	const handleLogin = async (userData) => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData, {
				skipAuthRefresh: true,
			});
			setStoredToken(data.token);
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push(isMasterUser(data.user) ? MASTER_HOME : "/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const updateChatLayoutMode = (nextUser) => {
		if (nextUser && typeof nextUser === "object") {
			setUser((prev) => ({
				...prev,
				...nextUser,
				chatLayoutMode: normalizeChatLayoutMode(
					nextUser.chatLayoutMode ?? prev.chatLayoutMode
				),
			}));
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout", { skipAuthRefresh: true });
		} catch (err) {
			// mesmo se o logout remoto falhar, limpa sessão local
		}

		setIsAuth(false);
		setUser({});
		clearStoredToken();
		resetGoWebSocket();
		disconnectAllSharedSockets();
		setLoading(false);
		history.push("/login");
	};

	return {
		isAuth,
		user,
		loading,
		handleLogin,
		handleLogout,
		updateChatLayoutMode,
	};
};

export default useAuth;
